// Deployment trigger: 2026-06-05
import { auth, secondaryAuth } from "../firebase";
import { createUserWithEmailAndPassword, updateProfile, signOut, sendPasswordResetEmail } from "firebase/auth";

export type UserRole = "admin" | "moderator" | "user";

export interface ManagedUser {
  uid: string;
  username?: string;
  email?: string;
  role: UserRole;
}

async function callManageUsers(action: string, payload: Record<string, any>) {
  const current = auth.currentUser;
  if (!current) throw new Error("You must be signed in.");
  const idToken = await current.getIdToken();

  const res = await fetch("/api/manage-users", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
    body: JSON.stringify({ action, ...payload }),
  });

  let data: any = {};
  try {
    data = await res.json();
  } catch {
    /* non-JSON */
  }
  if (!res.ok || data.error) {
    throw new Error(data.error || "Request failed. Please try again.");
  }
  return data;
}

/**
 * Create a new user. The Auth login is created client-side via a secondary
 * Firebase app (so the admin's own session is untouched and no Auth Admin IAM
 * role is needed); the server then writes the profile + role.
 */
export async function createUserAccount(p: {
  email: string;
  username: string;
  password: string;
  role: UserRole;
}): Promise<{ uid: string }> {
  const email = p.email.trim().toLowerCase();
  const username = p.username.trim() || email.split("@")[0];

  const cred = await createUserWithEmailAndPassword(secondaryAuth, email, p.password);
  const uid = cred.user.uid;
  try {
    await updateProfile(cred.user, { displayName: username });
  } catch {
    /* non-fatal */
  }
  // Don't keep the new user signed in on the secondary app.
  await signOut(secondaryAuth).catch(() => {});

  await callManageUsers("provision", { uid, email, username, role: p.role });
  return { uid };
}

export const updateUser = (p: { uid: string; username?: string; role?: UserRole }) =>
  callManageUsers("update", p);

/** Remove a user from the app (deletes their profile/role; the login remains). */
export const removeUserFromApp = (p: { uid: string }) => callManageUsers("removeFromApp", p);

/** Send a Firebase password-reset email. Public operation — no special permission. */
export const sendResetEmail = (email: string) => sendPasswordResetEmail(auth, email);
